import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  ButtonIcon,
  IconEdit,
  Stack,
  Text,
} from 'braid-design-system';
import type {
  ComponentProps,
  Dispatch,
  ReactNode,
  SetStateAction,
} from 'react';

import { ConfirmableDrawer } from 'src/ui/ConfirmableDrawer/ConfirmableDrawer';

import translations from '../DocumentsSummary/.vocab';

interface PropsDrawerDocument {
  title: string;
  currentSelectionText: string;
  currentSelectionTestId: string;
  editButtonLabel: string;
  editButtonId: string;
  onSubmit: () => void;
  shouldConfirmBeforeClose: boolean;
  children: ReactNode;
  isFormOpen: boolean;
  setIsFormOpen: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
}

const DrawerDocument = ({
  title,
  currentSelectionText,
  currentSelectionTestId,
  editButtonLabel,
  editButtonId,
  onSubmit,
  shouldConfirmBeforeClose,
  children,
  isFormOpen,
  setIsFormOpen,
  onClose,
}: PropsDrawerDocument) => {
  const { t } = useTranslations(translations);

  return (
    <>
      <Box display="flex" width="full" justifyContent="spaceBetween">
        <Stack space="medium">
          <Text weight="strong">{title}</Text>
          <Text data={{ testid: currentSelectionTestId }}>
            {currentSelectionText}
          </Text>
        </Stack>
        <EditButton
          label={editButtonLabel}
          id={editButtonId}
          onClick={() => setIsFormOpen(true)}
          data={{ testid: editButtonId }}
        />
      </Box>
      <ConfirmableDrawer
        title={title}
        width="small"
        open={isFormOpen}
        onClose={onClose}
        shouldConfirmBeforeClose={shouldConfirmBeforeClose}
      >
        <Box component="form">
          <Stack space="large">
            <>{children}</>
            <Actions>
              <Button
                tone="formAccent"
                variant="solid"
                data={{ testid: 'save-document' }}
                onClick={onSubmit}
              >
                {t('Save')}
              </Button>
              <Button
                type="reset"
                tone="formAccent"
                variant="transparent"
                onClick={onClose}
                data={{ testid: 'cancel-document' }}
              >
                {t('Cancel')}
              </Button>
            </Actions>
          </Stack>
        </Box>
      </ConfirmableDrawer>
    </>
  );
};

const EditButton = (
  props: Omit<ComponentProps<typeof ButtonIcon>, 'tone' | 'variant' | 'icon'>,
) => (
  <ButtonIcon
    tone="neutral"
    variant="transparent"
    icon={<IconEdit />}
    bleed
    {...props}
  />
);

export default DrawerDocument;
